import React, { useState, useEffect } from 'react';
import { auth, functions } from '../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import AddressAutocompleteInput from './AddressAutocompleteInput';

const NewProjectModal = ({ isOpen, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [projectCode, setProjectCode] = useState('');
  const [, setDuplicateProjects] = useState([]);
  const [showWarning, setShowWarning] = useState(false); // State for showing the warning popup
  const [country, setCountry] = useState('UK'); // Added country state

  const setLatLng = (lat, lng) => {
    setLatitude(lat);
    setLongitude(lng);
  };

  useEffect(() => {
    if (isOpen) {
      const generatedCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      setProjectCode(generatedCode);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCreateProject = async (e) => {
    e.preventDefault();

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("You must be logged in.");
      console.log('🔐 Current user:', user.uid, user.email);

      // Call Cloud Function to create project
      console.log('🔍 Calling createProject Cloud Function');
      const createProjectFunction = httpsCallable(functions, 'createProject');
      
      const functionData = {
        name: name.trim(),
        address: address.trim(),
        postcode: postcode, // Cloud Function will format it
        projectCode: projectCode,
        country: country,
      };

      // Add coordinates if geocoding was successful
      if (latitude != null && longitude != null) {
        functionData.latitude = latitude;
        functionData.longitude = longitude;
      }

      const result = await createProjectFunction(functionData);
      const resultData = result.data;

      if (!resultData.success) {
        throw new Error(resultData.message || 'Failed to create project');
      }

      console.log('✅ Project created via Cloud Function:', resultData.projectId);
      
      // Success!
      setName('');
      setAddress('');
      setPostcode('');
      setLatitude(null);
      setLongitude(null);
      setProjectCode('');
      setDuplicateProjects([]);
      setShowWarning(false);
      onClose();
      onCreate();
    } catch (error) {
      console.error('Error creating project:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      alert(`Failed to create project: ${error.message}`);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 999
    }}>
      <div style={{
        background: 'var(--cc-card-bg)',
        padding: '30px',
        borderRadius: '10px',
        width: '90%',
        maxWidth: '400px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <h2 style={{ margin: 0, fontSize: '22px', color: 'var(--cc-text-primary)' }}>Create new project</h2>
        <form onSubmit={handleCreateProject} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={{
              padding: '12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px'
            }}
          />
          <AddressAutocompleteInput
            address={address}
            setAddress={setAddress}
            setPostcode={setPostcode}
            setLatLng={setLatLng}
            placeholder="Address"
          />
          <input
            type="text"
            placeholder="Postcode"
            value={postcode}
            onChange={(e) => { setPostcode(e.target.value); setLatLng(null, null); }}
            required
            style={{
              padding: '12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px'
            }}
          />
          <select
            value={country}
            onChange={e => setCountry(e.target.value)}
            required
            style={{
              padding: '12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px'
            }}
            disabled
          >
            <option value="UK">United Kingdom</option>
          </select>
          <input
            type="text"
            value={projectCode}
            readOnly
            style={{
              padding: '12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              backgroundColor: 'var(--branding-app-surface)',
              color: 'var(--cc-text-secondary)',
              fontWeight: 'bold'
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--cc-text-secondary)',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                background: 'var(--branding-app-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Create
            </button>
          </div>
        </form>
        {showWarning && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '8px',
            color: '#92400e',
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '14px' // Reduced font size
          }}>
            Warning: There are existing projects at this postcode. Please double-check!<br />
            <span style={{ fontWeight: 'normal' }}>Press Create again to create the project.</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewProjectModal;
